import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { User, Users, Workflow as WorkflowIcon } from 'lucide-react';
import { useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api, type AnnouncementDetail, type DepartmentRef, type ProjectRef, type UserRef } from '../lib/api';
import { P, useAuth } from '../lib/auth';
import { enumLabel, useI18n } from '../lib/i18n';
import { refusalText } from '../lib/reach';
import { formatDateTime, formatNumber, toDateTimeLocal } from '../lib/format';
import { FormGrid, FormPage } from '../components/FormPage';
import { Field, Input, Select, Textarea, useToast } from '../components/ui';
import { AttachmentList, AttachmentsField, useUploads } from '../components/Attachments';
import { NameField, hasDescription, nameFits } from '../components/NameField';
import { TaskChecklistSection } from '../components/TaskChecklist';
import { isDepartmentTask, useTaskHolderName } from '../components/TaskHolder';
import {
  WorkflowBuilder,
  everyoneOnLevels,
  levelsReady,
  startLevels,
  workflowPayload,
  type LevelDraft,
} from '../components/WorkflowBuilder';
import { clientName, type ChangeRequestDetail } from './ClientCrs';
import { withCurrent } from './TicketForm';
import { taskPath } from '../components/LeaderTaskPanels';

const PRIORITIES = ['LOW', 'MEDIUM', 'HIGH', 'URGENT'];

/**
 * Route: `/tasks/new`, optionally `?projectId=…` when started from a board that
 * is already filtered to one project, or `?announcementId=…` from an
 * announcement's "Create task" — the task starts from its title and details,
 * for the person making it, and points back at it.
 *
 * Given `changeRequestId` (route `/client-crs/:id/convert`), it converts an
 * approved client change request: the task starts from the request's title and
 * details, on one of the client's projects, carries the request's files, and
 * needs a person or a workflow, a start and a deadline.
 *
 * Under Assignee, beside the people, a whole department may be chosen
 * (2026-10-10) — one of the creator's own, any for an administrator: everyone in
 * it shares the task. It is then the task's department too.
 *
 * Or a workflow, built right here (operator, 2026-10-15: "i want to build this
 * workflow as is it but in the task it self"): people in levels, from the same
 * people the task could go to — its project's, on a project task.
 */
export function TaskFormPage({ changeRequestId = '' }: { changeRequestId?: string } = {}) {
  const { t, locale, localized } = useI18n();
  const { can, user } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const converting = !!changeRequestId;

  const [form, setForm] = useState({
    title: '',
    description: '',
    projectId: searchParams.get('projectId') ?? '',
    assigneeUserId: '',
    // Through a workflow built on the form, its levels below (2026-10-15).
    toWorkflow: false,
    departmentId: '',
    // Everyone in `departmentId` shares it (2026-10-10).
    toDepartment: false,
    priority: 'MEDIUM',
    startAt: '',
    dueAt: '',
  });
  const [levels, setLevels] = useState<LevelDraft[]>(startLevels);
  const uploads = useUploads();
  const datesBackwards = !!form.startAt && !!form.dueAt && new Date(form.dueAt) <= new Date(form.startAt);

  const announcementId = searchParams.get('announcementId') ?? '';
  const source = useQuery({
    queryKey: ['announcement', announcementId],
    queryFn: () => api.get<AnnouncementDetail>(`/announcements/${announcementId}`),
    enabled: !!announcementId,
  });
  const [prefilled, setPrefilled] = useState(false);
  if (source.data && !prefilled) {
    setPrefilled(true);
    const { title, body } = source.data;
    setForm((f) => ({
      ...f,
      title: f.title || title,
      description: f.description || (body ?? ''),
      assigneeUserId: f.assigneeUserId || user?.id || '',
    }));
  }

  // The client's change request being converted: its words to start from.
  const changeRequest = useQuery({
    queryKey: ['client-cr', changeRequestId],
    queryFn: () => api.get<ChangeRequestDetail>(`/client-change-requests/${changeRequestId}`),
    enabled: converting,
  });
  const request = changeRequest.data;
  if (request && !prefilled) {
    setPrefilled(true);
    setForm((f) => ({ ...f, title: f.title || request.title, description: f.description || (request.details ?? '') }));
  }

  // A converted request's task goes on one of the client's projects — those the
  // person converting it is on — or on none.
  const clientId = request?.client.id;
  const { data: projects } = useQuery({
    queryKey: converting ? ['projects', 'options', { clientId }] : ['projects', 'options'],
    queryFn: () => api.get<ProjectRef[]>('/projects/options', clientId ? { clientId } : undefined),
    enabled: !converting || !!clientId,
  });
  // The request's own project, once the list shows whether it can be chosen.
  const [projectPrefilled, setProjectPrefilled] = useState(false);
  if (request && projects && !projectPrefilled) {
    setProjectPrefilled(true);
    const own = request.project;
    if (own && projects.some((p) => p.id === own.id)) setForm((f) => ({ ...f, projectId: f.projectId || own.id }));
  }
  const requestProjectHidden = !!request?.project && !!projects && !projects.some((p) => p.id === request.project!.id);
  // §8.4 — a project task goes to someone on the project; a general task can go
  // to anyone. The project's list is keyed under the project, so adding or
  // removing a member (which invalidates `['project', id]`) refreshes it.
  const { data: assignees } = useQuery({
    queryKey: form.projectId ? ['project', form.projectId, 'assignees'] : ['users', 'options'],
    queryFn: () => api.get<UserRef[]>('/users/options', { projectId: form.projectId }),
  });
  // The departments' names, readable by whoever raises tickets or creates tasks
  // (`departments.read` or not: an Employee holds only `tasks.create`).
  const { data: departments } = useQuery({
    queryKey: ['departments', 'picker'],
    queryFn: () => api.get<DepartmentRef[]>('/departments/options'),
  });

  // A workflow built here decides who gets the task: everyone on its first level.
  const workflowOn = form.toWorkflow;
  // §8.4 again, on every level — the server refuses it too, unless the creator may override. The picker
  // offers only the project's people, so whoever is off it was put on before the project was chosen.
  const notOnProject = new Set(
    workflowOn && form.projectId && assignees
      ? everyoneOnLevels(levels).filter((id) => !assignees.some((a) => a.id === id))
      : [],
  );
  const overrides = can(P.TASKS_MANAGE_ALL);
  // A whole department, under Assignee: one of the creator's — not when converting a change request,
  // which goes to a person or a workflow. (The list may be cached from another page, so it is gated here.)
  const ownDepartments = useSharableDepartments(!converting).list;
  const sharable = converting ? [] : ownDepartments;
  const sharedWith = !workflowOn && form.toDepartment ? (departments ?? []).find((d) => d.id === form.departmentId) ?? null : null;
  const audience = useDepartmentAudience(sharedWith?.id ?? '', form.projectId || null);
  const pickAssignee = (value: string) =>
    value === WORKFLOW_CHOICE
      ? setForm({ ...form, assigneeUserId: '', toDepartment: false, toWorkflow: true })
      : value.startsWith('department:')
        ? setForm({
            ...form,
            assigneeUserId: '',
            toWorkflow: false,
            toDepartment: true,
            departmentId: value.slice('department:'.length),
          })
        : setForm({ ...form, assigneeUserId: value, toDepartment: false, toWorkflow: false });
  // Converting needs the work given to someone, and both dates.
  const incomplete = converting && (!form.startAt || !form.dueAt || (!workflowOn && !form.assigneeUserId));

  const create = useMutation({
    mutationFn: () => {
      const task = {
        title: form.title,
        description: form.description,
        projectId: form.projectId || undefined,
        ...(workflowOn
          ? { workflow: workflowPayload(levels) }
          : sharedWith
            ? { assignedToDepartment: true }
            : { assigneeUserId: form.assigneeUserId || undefined }),
        departmentId: form.departmentId || undefined,
        priority: form.priority,
        // datetime-local is the browser's own clock; the API wants an instant.
        startAt: form.startAt ? new Date(form.startAt).toISOString() : undefined,
        dueAt: form.dueAt ? new Date(form.dueAt).toISOString() : undefined,
        fileIds: uploads.fileIds.length ? uploads.fileIds : undefined,
      };
      return converting
        ? api.post(`/client-change-requests/${changeRequestId}/convert`, task)
        : api.post('/tasks', { ...task, sourceAnnouncementId: source.data?.id });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['kanban'] });
      void queryClient.invalidateQueries({ queryKey: ['tasks'] });
      if (announcementId) {
        void queryClient.invalidateQueries({ queryKey: ['announcement', announcementId] });
        void queryClient.invalidateQueries({ queryKey: ['announcements'] });
      }
      if (converting) {
        void queryClient.invalidateQueries({ queryKey: ['client-cr', changeRequestId] });
        void queryClient.invalidateQueries({ queryKey: ['client-crs'] });
        toast.success(t('crs.converted'));
        navigate(`/client-crs/${changeRequestId}`);
        return;
      }
      navigate(-1);
    },
    onError: (e: any) => toast.error(refusalText(e, t)),
  });

  return (
    <FormPage
      title={converting ? t('crs.convertTitle') : t('tasks.new')}
      backTo={converting ? `/client-crs/${changeRequestId}` : undefined}
      backLabel={converting ? (request?.number ?? t('nav.clientCrs')) : source.data ? source.data.title : t('nav.tasks')}
      summary={
        request ? (
          <div>
            <span className="text-caption uppercase tracking-wide text-[var(--text-tertiary)]">{t('crs.fromRequest')}</span>
            <Link
              to={`/client-crs/${request.id}`}
              className="mt-0.5 block truncate text-title text-primary-600 dark:text-primary-400 hover:underline"
              dir="auto"
            >
              <span className="font-mono">{request.number}</span> — {request.title}
            </Link>
            <p className="mt-0.5 text-caption text-muted">
              {clientName(request.client, locale)}
              {request.project ? ` · ${localized(request.project)}` : ''}
            </p>
            {!request.canConvert && (
              <p className="mt-2 rounded-md border border-warning/35 bg-warning/8 px-3 py-2 text-body-sm text-warning-fg dark:text-warning-dark">
                {t('crs.cannotConvert')}
              </p>
            )}
            {request.attachments.length > 0 && (
              <div className="mt-3">
                <p className="mb-1.5 text-caption text-muted">{t('crs.filesGoWithTask')}</p>
                <AttachmentList attachments={request.attachments} />
              </div>
            )}
          </div>
        ) : (
          source.data && (
            <div>
              <span className="text-caption uppercase tracking-wide text-[var(--text-tertiary)]">{t('tasks.fromAnnouncement')}</span>
              <Link
                to={`/announcements/${source.data.id}`}
                className="mt-0.5 block truncate text-title text-primary-600 dark:text-primary-400 hover:underline"
                dir="auto"
              >
                {source.data.title}
              </Link>
              <p className="mt-0.5 text-caption text-muted">{t('tasks.fromAnnouncementHint')}</p>
            </div>
          )
        )
      }
      submitLabel={converting ? t('crs.convert') : t('common.create')}
      submitting={create.isPending}
      loading={(!!announcementId && source.isLoading) || (converting && changeRequest.isLoading)}
      error={source.error ?? changeRequest.error}
      onRetry={() => void (converting ? changeRequest.refetch() : source.refetch())}
      // Saving while a file is still uploading would leave it behind.
      disabled={
        !nameFits(form.title) ||
        !hasDescription(form.description) ||
        uploads.busy ||
        datesBackwards ||
        (workflowOn && !levelsReady(levels)) ||
        (notOnProject.size > 0 && !overrides) ||
        (!!sharedWith && !audience.ready) ||
        incomplete ||
        (converting && !request?.canConvert)
      }
      onSubmit={() => create.mutate()}
    >
      <div className="space-y-4">
        <NameField label={t('common.name')} value={form.title} onChange={(title) => setForm({ ...form, title })} autoFocus />
        <Field label={t('common.description')} required>
          <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </Field>
        <FormGrid>
          {/* §8.1 — no project makes it a general task. Changing the project clears the
              assignee: whoever was picked may not be on the new one. */}
          <Field
            label={t('common.project')}
            hint={
              converting && requestProjectHidden && request?.project
                ? t('crs.projectNotYours', { name: localized(request.project) })
                : t('tasks.generalHint')
            }
          >
            <Select
              value={form.projectId}
              onChange={(e) => setForm({ ...form, projectId: e.target.value, assigneeUserId: '' })}
            >
              <option value="">{t('common.general')}</option>
              {(projects ?? []).map((p) => (
                <option key={p.id} value={p.id}>{localized(p)}</option>
              ))}
            </Select>
          </Field>
          <Field
            label={t('common.assignee')}
            required={converting}
            hint={
              workflowOn
                ? t('tasks.workflowBelow')
                : converting && !form.assigneeUserId
                  ? t('crs.assigneeOrWorkflow')
                  : form.projectId
                    ? t('tasks.projectMemberHint')
                    : undefined
            }
          >
            <Select
              value={workflowOn ? WORKFLOW_CHOICE : sharedWith ? `department:${sharedWith.id}` : form.assigneeUserId}
              onChange={(e) => pickAssignee(e.target.value)}
            >
              {/* People in levels, built below (2026-10-15) — first in the list (operator, the same day). */}
              <option value={WORKFLOW_CHOICE}>{t('tasks.throughWorkflow')}</option>
              {/* A converted request's work is given to someone, or to a workflow. */}
              <option value="">{converting ? '—' : t('common.unassigned')}</option>
              <optgroup label={t('tasks.people')}>
                {(assignees ?? []).map((u) => (
                  <option key={u.id} value={u.id}>{localized(u)}</option>
                ))}
              </optgroup>
              {/* Everyone in it shares the task (2026-10-10). */}
              {sharable.length > 0 && (
                <optgroup label={t('tasks.wholeDepartment')}>
                  {sharable.map((d) => (
                    <option key={d.id} value={`department:${d.id}`}>
                      {t('tasks.everyoneIn', { department: localized(d) })}
                    </option>
                  ))}
                </optgroup>
              )}
            </Select>
          </Field>
          <Field label={t('common.department')} hint={sharedWith ? t('tasks.departmentIsAssignee') : undefined}>
            <Select
              value={form.departmentId}
              onChange={(e) => setForm({ ...form, departmentId: e.target.value })}
              disabled={!!sharedWith}
            >
              <option value="">—</option>
              {(departments ?? []).map((d) => (
                <option key={d.id} value={d.id}>{localized(d)}</option>
              ))}
            </Select>
          </Field>
          <Field label={t('common.priority')}>
            <Select value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
              {PRIORITIES.map((p) => (
                <option key={p} value={p}>{enumLabel(p, locale)}</option>
              ))}
            </Select>
          </Field>
          <Field
            label={t('tasks.startDate')}
            required={converting}
            hint={workflowOn ? t('tasks.workflowDatesHint') : undefined}
          >
            <Input
              type="datetime-local"
              value={form.startAt}
              onChange={(e) => setForm({ ...form, startAt: e.target.value })}
            />
          </Field>
          <Field
            label={t('tasks.deadline')}
            required={converting}
            error={datesBackwards ? t('tasks.dueAfterStart') : null}
          >
            <Input
              type="datetime-local"
              value={form.dueAt}
              min={form.startAt || undefined}
              onChange={(e) => setForm({ ...form, dueAt: e.target.value })}
            />
          </Field>
        </FormGrid>
        {sharedWith && <DepartmentGoesTo department={sharedWith} audience={audience} />}
        {workflowOn && (
          <TaskWorkflowField
            levels={levels}
            onChange={setLevels}
            people={assignees ?? []}
            notOnProject={notOnProject}
            overrides={overrides}
          />
        )}
        <AttachmentsField uploads={uploads} />
      </div>
    </FormPage>
  );
}

/** The Assignee choice that builds a workflow below instead (2026-10-15). */
const WORKFLOW_CHOICE = 'workflow';

/**
 * A workflow built on the task's form (2026-10-15), and what the form says
 * about it: everyone on level 1 gets a copy of their own — or, given `copies`,
 * whatever the form says instead — and who is off the task's project (§8.4),
 * which the server refuses unless the creator may override.
 */
function TaskWorkflowField({
  levels,
  onChange,
  people,
  notOnProject,
  overrides,
  copies,
  disabled,
}: {
  levels: LevelDraft[];
  onChange: (levels: LevelDraft[]) => void;
  people: UserRef[];
  notOnProject?: ReadonlySet<string>;
  overrides?: boolean;
  copies?: string;
  disabled?: boolean;
}) {
  const { t } = useI18n();
  const off = (notOnProject?.size ?? 0) > 0;
  return (
    <div className="rounded-lg border hairline p-4">
      <WorkflowBuilder
        levels={levels}
        onChange={onChange}
        people={people}
        flagged={notOnProject}
        flagLabel={t('leaderTasks.notOnProject')}
        disabled={disabled}
        footer={
          <>
            {(levels[0]?.userIds.length ?? 0) > 1 && (
              <p className="mt-3 text-caption text-muted">{copies ?? t('tasks.eachGetsCopy')}</p>
            )}
            {off && (
              <p className={clsx('mt-2 text-caption', overrides ? 'text-warning-fg dark:text-warning-dark' : 'text-error-fg dark:text-error-dark')}>
                {overrides ? t('tasks.workflowOffProjectOverride') : t('tasks.workflowOffProject')}
              </p>
            )}
          </>
        }
      />
    </div>
  );
}

/**
 * Route: `/tasks/:id/deadline`. A workflow hands a task on with its start fixed
 * — the moment the level before finished — and no deadline: its new person
 * sets it here. Whoever created the task, or `tasks.manage_all`, may change it.
 * All of that only while nobody has started on it: once someone has, nobody,
 * admins included (operator, 2026-10-25), and the page shows it locked.
 */
export function TaskDeadlinePage() {
  const { id = '' } = useParams();
  const { t, locale } = useI18n();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [picked, setPicked] = useState<string | null>(null);

  const task = useQuery({ queryKey: ['task', id], queryFn: () => api.get<any>(`/tasks/${id}`) });
  const saved = toDateTimeLocal(task.data?.dueAt);
  const dueAt = picked ?? saved;
  const startAt: string | null = task.data?.startAt ?? null;
  const backwards = !!startAt && !!dueAt && new Date(dueAt) <= new Date(startAt);
  // A step a workflow handed on started when the level before it finished.
  const handedOn = task.data?.workflowLevel > 1;
  // A Leaders Task's page is under Leaders Tasks — the address says so before the task has loaded.
  const { pathname } = useLocation();
  const leaders = task.data ? !!task.data.isLeaderTask : pathname.startsWith('/leader-tasks');
  const home = taskPath({ id, isLeaderTask: leaders });
  // The server's rule for this person (`can.setDeadline`): never once someone has started on it (2026-10-25).
  const locked = !!task.data && !task.data.can?.setDeadline;
  const status: string | undefined = task.data?.status;

  const save = useMutation({
    mutationFn: () => api.post(`/tasks/${id}/deadline`, { dueAt: new Date(dueAt).toISOString() }),
    onSuccess: () => {
      for (const queryKey of [['task', id], ['tasks'], ['kanban'], ['leader-tasks']]) {
        void queryClient.invalidateQueries({ queryKey });
      }
      toast.success(t('tasks.deadlineSaved'));
      navigate(home);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <FormPage
      title={saved ? t('tasks.changeDeadline') : t('tasks.setDeadline')}
      subtitle={task.data?.title}
      backTo={home}
      backLabel={task.data?.title ?? t(leaders ? 'nav.leaderTasks' : 'nav.tasks')}
      submitLabel={t('common.save')}
      submitting={save.isPending}
      disabled={locked || !dueAt || backwards || dueAt === saved}
      loading={task.isLoading}
      error={task.error}
      onRetry={() => void task.refetch()}
      onSubmit={() => save.mutate()}
    >
      {locked && (
        <p className="mb-4 rounded-md border border-warning/35 bg-warning/8 px-3 py-2 text-body-sm text-warning-fg dark:text-warning-dark">
          {status === 'TO_DO'
            ? t('tasks.deadlineNotYours')
            : status === 'DONE' || status === 'CANCELLED'
              ? t('tasks.deadlineFinishedLocked')
              : t('tasks.deadlineStartedLocked')}
        </p>
      )}
      <FormGrid>
        <Field
          label={t('tasks.startDate')}
          hint={handedOn ? t('tasks.startedWhen', { n: formatNumber(task.data.workflowLevel - 1, locale) }) : undefined}
        >
          {/* Fixed: the deadline is what is being set. */}
          <Input value={startAt ? formatDateTime(startAt, locale) : '—'} disabled readOnly className="tabular" />
        </Field>
        <Field label={t('tasks.deadline')} required error={backwards ? t('tasks.dueAfterStart') : null}>
          <Input
            type="datetime-local"
            value={dueAt}
            min={toDateTimeLocal(startAt) || undefined}
            onChange={(e) => setPicked(e.target.value)}
            disabled={locked}
            autoFocus
          />
        </Field>
      </FormGrid>
    </FormPage>
  );
}

type EditForm = {
  title: string;
  description: string;
  projectId: string;
  departmentId: string;
  priority: string;
  startAt: string;
  dueAt: string;
};

/**
 * Route: `/tasks/:id/edit`. A task can be edited while nobody has started on it
 * — while it is To do — by whoever created it, a team leader or an admin; once
 * someone has, by nobody, admins included (operator, 2026-10-25), and the form
 * shows it locked. Who does it changes with Assign or Reassign, and its files
 * are added on its page, so the form has neither. A workflow's task stays on the
 * project its work started on; one made from a client's change request, on one
 * of that client's.
 *
 * Below the fields, its to-do list (operator, 2026-10-09): changed until it is
 * finished — on the task's page too — each change saved as it is made; the
 * page's Save is the fields'.
 *
 * A task its whole department shares (2026-10-10) keeps its department: Reassign
 * gives it to another. Moved to a project, someone of the department must be on it.
 */
export function TaskEditPage() {
  const { id = '' } = useParams();
  const { t, locale, localized } = useI18n();
  const { can } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const holderName = useTaskHolderName();

  const existing = useQuery({ queryKey: ['task', id], queryFn: () => api.get<any>(`/tasks/${id}`) });
  const task = existing.data;
  const [form, setForm] = useState<EditForm>({
    title: '',
    description: '',
    projectId: '',
    departmentId: '',
    priority: 'MEDIUM',
    startAt: '',
    dueAt: '',
  });
  // The task as it was when the form opened: only what moved from it is sent.
  const [initial, setInitial] = useState<EditForm | null>(null);
  if (task && !initial) {
    const loaded: EditForm = {
      title: task.title,
      description: task.description ?? '',
      projectId: task.projectId ?? '',
      departmentId: task.departmentId ?? '',
      priority: task.priority,
      startAt: toDateTimeLocal(task.startAt),
      dueAt: toDateTimeLocal(task.dueAt),
    };
    setInitial(loaded);
    setForm(loaded);
  }
  const locked = !!task && !task.can?.edit;
  const inWorkflow = !!task?.workflowRunId;
  // A Leaders Task: its page is under Leaders Tasks — the address says so before the task has
  // loaded — and its department is its leader's.
  const { pathname } = useLocation();
  const leaders = task ? !!task.isLeaderTask : pathname.startsWith('/leader-tasks');
  const home = taskPath({ id, isLeaderTask: leaders });
  const requestClient = task?.sourceChangeRequest?.client ?? null;

  // A task from a change request moves only among the client's projects.
  const { data: projects } = useQuery({
    queryKey: requestClient ? ['projects', 'options', { clientId: requestClient.id }] : ['projects', 'options'],
    queryFn: () => api.get<ProjectRef[]>('/projects/options', requestClient ? { clientId: requestClient.id } : undefined),
    enabled: !!task && !inWorkflow,
  });
  const { data: departments } = useQuery({
    queryKey: ['departments', 'picker'],
    queryFn: () => api.get<DepartmentRef[]>('/departments/options'),
  });
  // What the task already names stays on offer, even if the lists no longer carry it.
  const projectChoices = withCurrent(projects ?? [], task?.project ?? null);
  const departmentChoices = withCurrent(departments ?? [], task?.department ?? null);

  // §8.4 — moving it to a project whoever has it is not on.
  const movingProject = !!initial && !!form.projectId && form.projectId !== initial.projectId;
  const { data: projectPeople } = useQuery({
    queryKey: ['project', form.projectId, 'assignees'],
    queryFn: () => api.get<UserRef[]>('/users/options', { projectId: form.projectId }),
    enabled: movingProject && !!task?.assigneeUserId,
  });
  const assigneeName = holderName(task) ?? t('common.unassigned');
  const assigneeOff =
    movingProject && !!task?.assigneeUserId && !!projectPeople && !projectPeople.some((p) => p.id === task.assigneeUserId);
  // Its whole department shares it: moved to a project, someone of the department must be on it (§7.6).
  const shared = isDepartmentTask(task);
  const audience = useDepartmentAudience(shared && movingProject ? task.departmentId : '', form.projectId || null);
  const overrides = can(P.TASKS_MANAGE_ALL);
  const datesBackwards = !!form.startAt && !!form.dueAt && new Date(form.dueAt) <= new Date(form.startAt);
  const moved = (key: keyof EditForm) => !!initial && form[key] !== initial[key];
  const dirty = (Object.keys(form) as Array<keyof EditForm>).some(moved);

  const save = useMutation({
    mutationFn: () => {
      // datetime-local is the browser's own clock; the API wants an instant — or null, to clear it.
      const instant = (local: string) => (local ? new Date(local).toISOString() : null);
      return api.patch<any>(`/tasks/${id}`, {
        title: form.title,
        description: form.description,
        ...(inWorkflow ? {} : { projectId: form.projectId || null }),
        // A department's task keeps its department: Reassign moves it.
        ...(shared ? {} : { departmentId: form.departmentId || null }),
        priority: form.priority,
        // A date goes only when it was moved: the form shows minutes, a saved one may carry seconds.
        ...(moved('startAt') ? { startAt: instant(form.startAt) } : {}),
        ...(moved('dueAt') ? { dueAt: instant(form.dueAt) } : {}),
      });
    },
    onSuccess: () => {
      for (const queryKey of [['task', id], ['tasks'], ['kanban'], ['project'], ['leader-tasks']]) {
        void queryClient.invalidateQueries({ queryKey });
      }
      toast.success(t('tasks.saved'));
      // In place of the form, so going back from the task does not reopen it.
      navigate(home, { replace: true });
    },
    onError: (e: Error) => toast.error(refusalText(e, t)),
  });

  const projectHint = inWorkflow
    ? t('tasks.workflowProjectFixed')
    : assigneeOff && overrides
      ? t('tasks.assigneeNotOnProjectOverride', { name: assigneeName })
      : requestClient
        ? t('tasks.changeRequestProjects', { client: clientName(requestClient, locale) })
        : t('tasks.generalHint');

  return (
    <FormPage
      title={t('tasks.edit')}
      subtitle={task ? (locked ? task.title : t(leaders ? 'tasks.editableUntilStarted' : 'tasks.editableWithList')) : undefined}
      backTo={home}
      backLabel={task?.title ?? t(leaders ? 'nav.leaderTasks' : 'nav.tasks')}
      submitLabel={t('common.save')}
      submitting={save.isPending}
      disabled={
        locked ||
        !dirty ||
        !nameFits(form.title) ||
        !hasDescription(form.description) ||
        datesBackwards ||
        (assigneeOff && !overrides) ||
        audience.nobodyOnProject
      }
      loading={existing.isLoading}
      error={existing.error}
      onRetry={() => void existing.refetch()}
      onSubmit={() => save.mutate()}
    >
      <div className="space-y-4">
        {locked && (
          <p className="rounded-md border border-warning/35 bg-warning/8 px-3 py-2 text-body-sm text-warning-fg dark:text-warning-dark">
            {/* To do, not theirs to edit; started, nobody's — any task since 2026-10-25, a Leaders Task always. */}
            {task.status === 'TO_DO'
              ? t(leaders ? 'leaderTasks.editNotYours' : 'tasks.editNotYours')
              : !leaders && ['DONE', 'CANCELLED'].includes(task.status)
                ? t('tasks.finishedLocked')
                : t('tasks.startedLocked')}
          </p>
        )}
        <NameField
          label={t('common.name')}
          value={form.title}
          onChange={(title) => setForm({ ...form, title })}
          disabled={locked}
          autoFocus
        />
        <Field label={t('common.description')} required>
          <Textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            disabled={locked}
          />
        </Field>
        <FormGrid>
          <Field
            label={t('common.project')}
            hint={projectHint}
            error={
              assigneeOff && !overrides
                ? t('tasks.assigneeNotOnProject', { name: assigneeName })
                : audience.nobodyOnProject
                  ? t('tasks.departmentNobodyOnProject', { department: localized(task?.department) })
                  : null
            }
          >
            {inWorkflow ? (
              <Input value={task?.project ? localized(task.project) : t('common.general')} disabled readOnly />
            ) : (
              <Select
                value={form.projectId}
                onChange={(e) => setForm({ ...form, projectId: e.target.value })}
                disabled={locked}
              >
                <option value="">{t('common.general')}</option>
                {projectChoices.map((p) => (
                  <option key={p.id} value={p.id}>{localized(p)}</option>
                ))}
              </Select>
            )}
          </Field>
          {/* Who does it is not the edit form's: Reassign, on the task's page, changes it. */}
          <Field
            label={leaders ? t('leaderTasks.askedOf') : t('common.assignee')}
            hint={leaders ? t('leaderTasks.leaderByReassign') : t('tasks.assigneeByReassign')}
          >
            <Input value={task ? assigneeName : ''} disabled readOnly />
          </Field>
          {leaders || shared ? (
            // The department its leader leads — it moves with them, by "Give to another leader" — or the one that
            // shares it, which Reassign changes.
            <Field label={t('common.department')} hint={leaders ? t('leaderTasks.departmentFixed') : t('tasks.departmentShared')}>
              <Input value={localized(task?.department)} disabled readOnly />
            </Field>
          ) : (
            <Field label={t('common.department')}>
              <Select
                value={form.departmentId}
                onChange={(e) => setForm({ ...form, departmentId: e.target.value })}
                disabled={locked}
              >
                <option value="">—</option>
                {departmentChoices.map((d) => (
                  <option key={d.id} value={d.id}>{localized(d)}</option>
                ))}
              </Select>
            </Field>
          )}
          <Field label={t('common.priority')}>
            <Select
              value={form.priority}
              onChange={(e) => setForm({ ...form, priority: e.target.value })}
              disabled={locked}
            >
              {PRIORITIES.map((p) => (
                <option key={p} value={p}>{enumLabel(p, locale)}</option>
              ))}
            </Select>
          </Field>
          <Field label={t('tasks.startDate')}>
            <Input
              type="datetime-local"
              value={form.startAt}
              onChange={(e) => setForm({ ...form, startAt: e.target.value })}
              disabled={locked}
            />
          </Field>
          <Field label={t('tasks.deadline')} error={datesBackwards ? t('tasks.dueAfterStart') : null}>
            <Input
              type="datetime-local"
              value={form.dueAt}
              min={form.startAt || undefined}
              onChange={(e) => setForm({ ...form, dueAt: e.target.value })}
              disabled={locked}
            />
          </Field>
        </FormGrid>
        {task && <TaskChecklistSection task={task} />}
      </div>
    </FormPage>
  );
}

/**
 * The departments someone may give a task to as a whole (2026-10-10), as the API
 * reaches them: their own — every one with users.view_all — of those switched on.
 */
function useSharableDepartments(enabled: boolean): { list: DepartmentRef[]; loaded: boolean } {
  const { user, can } = useAuth();
  const { data } = useQuery({
    queryKey: ['departments', 'picker'],
    queryFn: () => api.get<DepartmentRef[]>('/departments/options'),
    enabled,
  });
  const mine = new Set((user?.departments ?? []).map((d) => d.id));
  return { list: (data ?? []).filter((d) => can(P.USERS_VIEW_ALL) || mine.has(d.id)), loaded: !!data };
}

/**
 * Who a task given to a whole department reaches (2026-10-10): everyone in it —
 * on a project task only those of them on the project, who alone can see it
 * (§7.6). The API refuses a department nobody is in, and a project none of them
 * is on; `ready` says neither is the case.
 */
function useDepartmentAudience(departmentId: string, projectId: string | null) {
  const everyone = useQuery({
    queryKey: ['users', 'options', { departmentId }],
    queryFn: () => api.get<UserRef[]>('/users/options', { departmentId }),
    enabled: !!departmentId,
  });
  // Keyed under the project, so a change to its members refreshes it.
  const onProject = useQuery({
    queryKey: ['project', projectId, 'assignees', { departmentId }],
    queryFn: () => api.get<UserRef[]>('/users/options', { departmentId, projectId }),
    enabled: !!departmentId && !!projectId,
  });
  const people = everyone.data ?? [];
  const loaded = !!departmentId && everyone.isSuccess && (!projectId || onProject.isSuccess);
  const seeing = projectId ? (onProject.data ?? []) : people;
  return {
    people,
    notOnProject: projectId && onProject.data ? people.filter((p) => !onProject.data.some((o) => o.id === p.id)) : [],
    empty: loaded && people.length === 0,
    nobodyOnProject: loaded && !!projectId && people.length > 0 && seeing.length === 0,
    ready: loaded && seeing.length > 0,
  };
}

/** Who a task given to a whole department goes to: everyone in it, by name, and who of them cannot see it. */
function DepartmentGoesTo({
  department,
  audience,
}: {
  department: DepartmentRef;
  audience: ReturnType<typeof useDepartmentAudience>;
}) {
  const { t, locale, localized } = useI18n();
  const names = (list: UserRef[]) => list.map((p) => localized(p)).join(locale === 'ar' ? '، ' : ', ');
  const name = localized(department);
  return (
    <div>
      <span className="mb-1.5 block text-body-sm font-medium text-[var(--text-secondary)]">{t('tasks.goesTo')}</span>
      <p className="flex items-center gap-1.5 text-body text-[var(--text-primary)]">
        <Users className="h-4 w-4 shrink-0 text-[var(--text-tertiary)]" aria-hidden />
        {t('tasks.everyoneIn', { department: name })}
      </p>
      {audience.people.length > 0 && <p className="mt-0.5 text-caption text-muted">{names(audience.people)}</p>}
      {audience.empty && <p className="mt-1 text-caption text-error-fg dark:text-error-dark">{t('tasks.departmentEmpty', { department: name })}</p>}
      {audience.nobodyOnProject && (
        <p className="mt-1 text-caption text-error-fg dark:text-error-dark">{t('tasks.departmentNobodyOnProject', { department: name })}</p>
      )}
      {!audience.nobodyOnProject && audience.notOnProject.length > 0 && (
        <p className="mt-1 text-caption text-warning-fg dark:text-warning-dark">
          {t('tasks.departmentPartlyOnProject', { names: names(audience.notOnProject) })}
        </p>
      )}
    </div>
  );
}

type AssignMode = 'person' | 'department' | 'workflow';

/**
 * Routes: `/tasks/:id/assign` and `/tasks/:id/reassign`. Team leaders and admins
 * give an open task to someone — a project task to someone on its project
 * (§8.4) — or to someone else. Taking it from someone needs a reason: they are
 * told it, and the task's history keeps it.
 *
 * A task nobody has may go to a workflow instead (operator, 2026-10-07: "if the
 * task not assigned to anyone and clicked on assign i can assign to person or
 * workflow") — built here since 2026-10-15, people in levels: to everyone on its
 * level 1 — the task itself to the first of them, a copy to each other — and on
 * through the levels after, as a task created with a workflow goes.
 *
 * Or to a whole department (operator, 2026-10-10: "i want to assign to
 * department so everyone in this department can work on it"; asked, they chose
 * one task the department shares): one of the leader's own departments — any,
 * for an administrator — everyone in which sees it and works on it. One someone
 * has, or another department, goes to it too, with a reason.
 */
export function TaskAssignPage() {
  const { id = '' } = useParams();
  const { t, locale, localized } = useI18n();
  const { can } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const holderName = useTaskHolderName();
  const [picked, setMode] = useState<AssignMode>('person');
  const [assigneeUserId, setAssignee] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [levels, setLevels] = useState<LevelDraft[]>(startLevels);
  const [note, setNote] = useState('');

  const task = useQuery({ queryKey: ['task', id], queryFn: () => api.get<any>(`/tasks/${id}`) });
  const projectId: string | null = task.data?.projectId ?? null;
  // §8.4 — a project task goes to someone on the project; a general one to anyone.
  const { data: people } = useQuery({
    queryKey: projectId ? ['project', projectId, 'assignees'] : ['users', 'options'],
    queryFn: () => api.get<UserRef[]>('/users/options', { projectId }),
    enabled: !!task.data,
  });
  // Whoever has it now: a person, or everyone in a department (2026-10-10).
  const holder = task.data ? holderName(task.data) : null;
  const reassigning = !!holder;
  const allowed = !!(reassigning ? task.data?.can?.reassign : task.data?.can?.assign);
  const finished = !!task.data && ['DONE', 'CANCELLED'].includes(task.data.status);
  // Giving it to whoever has it would change nothing.
  const choices = (people ?? []).filter((p) => p.id !== task.data?.assigneeUserId);

  // A workflow is offered only where the API takes one: an open task nobody has,
  // not a step of a workflow already, not one a Q Task follows. Its people are
  // the ones the task could go to (2026-10-15).
  const offersWorkflow = !!task.data?.can?.assignWorkflow;
  // A department: on any open task but a workflow's step — not to the one that has it already, so
  // not offered at all when that is the leader's only department. Someone in none is told so.
  const departmentAllowed = !!task.data?.can?.assignDepartment;
  const sharedBy: string | null = isDepartmentTask(task.data) ? task.data.departmentId : null;
  const sharable = useSharableDepartments(departmentAllowed);
  const departments = sharable.list.filter((d) => d.id !== sharedBy);
  const offersDepartment = departmentAllowed && !(sharable.loaded && sharable.list.length > 0 && departments.length === 0);

  const modes = [
    { id: 'person' as const, label: t('tasks.toPerson'), hint: t('tasks.toPersonHint'), icon: User },
    ...(offersDepartment
      ? [{ id: 'department' as const, label: t('tasks.toDepartment'), hint: t('tasks.toDepartmentHint'), icon: Users }]
      : []),
    ...(offersWorkflow
      ? [{ id: 'workflow' as const, label: t('tasks.toWorkflow'), hint: t('tasks.toWorkflowHint'), icon: WorkflowIcon }]
      : []),
  ];
  const mode: AssignMode = modes.some((m) => m.id === picked) ? picked : 'person';
  const toWorkflow = mode === 'workflow';
  const toDepartment = mode === 'department';

  // Level 1's people, as the picker names them: the first gets this task itself.
  const levelOne = (levels[0]?.userIds ?? []).flatMap((uid) => (people ?? []).filter((p) => p.id === uid));
  const names = (list: UserRef[]) => list.map((p) => localized(p)).join(locale === 'ar' ? '، ' : ', ');
  const department = toDepartment ? (departments.find((d) => d.id === departmentId) ?? null) : null;
  const audience = useDepartmentAudience(department?.id ?? '', projectId);

  const run = useMutation({
    mutationFn: () =>
      api.post(
        `/tasks/${id}/assign`,
        toWorkflow
          ? { workflow: workflowPayload(levels) }
          : {
              ...(toDepartment ? { departmentId } : { assigneeUserId }),
              ...(reassigning ? { note: note.trim() } : {}),
            },
      ),
    onSuccess: () => {
      for (const queryKey of [['task', id], ['tasks'], ['kanban'], ['project']]) {
        void queryClient.invalidateQueries({ queryKey });
      }
      if (toWorkflow) {
        toast.success(t('tasks.sentToWorkflow', { names: names(levelOne) }));
      } else if (toDepartment && department) {
        toast.success(t('tasks.sentToDepartment', { department: localized(department) }));
      } else {
        const chosen = choices.find((p) => p.id === assigneeUserId);
        toast.success(t('tasks.assignedTo', { name: chosen ? localized(chosen) : '' }));
      }
      navigate(`/tasks/${id}`, { replace: true });
    },
    onError: (e: Error) => toast.error(refusalText(e, t)),
  });

  const ready = toWorkflow
    ? levelsReady(levels)
    : toDepartment
      ? !!department && audience.ready && (!reassigning || !!note.trim())
      : !!assigneeUserId && (!reassigning || !!note.trim());

  return (
    <FormPage
      title={reassigning ? t('tasks.reassignTitle') : t('tasks.assignTitle')}
      subtitle={task.data?.title}
      backTo={`/tasks/${id}`}
      backLabel={task.data?.title ?? t('nav.tasks')}
      submitLabel={reassigning ? t('tasks.reassign') : t('tasks.assign')}
      submitting={run.isPending}
      disabled={!allowed || !ready}
      loading={task.isLoading}
      error={task.error}
      onRetry={() => void task.refetch()}
      onSubmit={() => run.mutate()}
    >
      <div className="space-y-4">
        {task.data && !allowed && (
          <p className="rounded-md border border-warning/35 bg-warning/8 px-3 py-2 text-body-sm text-warning-fg dark:text-warning-dark">
            {finished ? t('tasks.finishedNoReassign') : t('tasks.assignNotYours')}
          </p>
        )}
        {allowed && reassigning && task.data?.workflowRunId && (
          <p className="rounded-md border border-info/30 bg-info/8 px-3 py-2 text-body-sm text-info-fg dark:text-info-dark">
            {t('tasks.reassignStepHint')}
          </p>
        )}
        {modes.length > 1 && (
          <div
            className={clsx('grid gap-2', modes.length > 2 ? 'sm:grid-cols-3' : 'sm:grid-cols-2')}
            role="radiogroup"
            aria-label={t('tasks.assignTo')}
          >
            {modes.map(({ id: option, label, hint, icon: Icon }) => (
              <label
                key={option}
                className={clsx(
                  'flex cursor-pointer gap-2.5 rounded-md border p-3 transition-colors duration-fast',
                  mode === option ? 'border-primary bg-primary/8' : 'hairline hover:border-primary/40',
                )}
              >
                <input
                  type="radio"
                  name="assign-to"
                  value={option}
                  checked={mode === option}
                  onChange={() => setMode(option)}
                  className="mt-0.5 h-4 w-4 shrink-0 accent-primary"
                />
                <span className="min-w-0">
                  <span className="flex items-center gap-1.5 text-body-sm font-medium text-[var(--text-primary)]">
                    <Icon className="h-4 w-4 shrink-0 text-[var(--text-tertiary)]" aria-hidden />
                    {label}
                  </span>
                  <span className="mt-0.5 block text-caption text-muted">{hint}</span>
                </span>
              </label>
            ))}
          </div>
        )}
        {toWorkflow ? (
          <>
            <p className="text-body-sm text-muted">{t('tasks.assignWorkflowHint')}</p>
            <TaskWorkflowField
              levels={levels}
              onChange={setLevels}
              // §8.4 — a project task's people are its project's; any other's, the leader's departments'.
              people={people ?? []}
              copies={levelOne[0] ? t('tasks.keepsThisTask', { name: localized(levelOne[0]) }) : undefined}
              disabled={!allowed}
            />
          </>
        ) : (
          <>
            <FormGrid>
              {holder && (
                <Field label={t('tasks.currentAssignee')}>
                  <Input value={holder} disabled readOnly />
                </Field>
              )}
              {toDepartment ? (
                <Field
                  label={t('common.department')}
                  required
                  hint={
                    departments.length
                      ? can(P.USERS_VIEW_ALL)
                        ? t('tasks.assignAnyDepartmentHint')
                        : t('tasks.assignDepartmentHint')
                      : undefined
                  }
                  error={sharable.loaded && !departments.length ? t('tasks.noDepartmentOfYours') : null}
                >
                  <Select
                    value={departmentId}
                    onChange={(e) => setDepartmentId(e.target.value)}
                    disabled={!allowed || !departments.length}
                    autoFocus
                  >
                    <option value="">—</option>
                    {departments.map((d) => (
                      <option key={d.id} value={d.id}>{localized(d)}</option>
                    ))}
                  </Select>
                </Field>
              ) : (
                <Field
                  label={reassigning ? t('tasks.newAssignee') : t('tasks.assignTo')}
                  required
                  hint={projectId ? t('tasks.projectMemberHint') : t('tasks.assignHint')}
                >
                  <Select value={assigneeUserId} onChange={(e) => setAssignee(e.target.value)} disabled={!allowed} autoFocus>
                    <option value="">—</option>
                    {choices.map((u) => (
                      <option key={u.id} value={u.id}>{localized(u)}</option>
                    ))}
                  </Select>
                </Field>
              )}
            </FormGrid>
            {department && <DepartmentGoesTo department={department} audience={audience} />}
            {reassigning && (
              <Field label={t('common.reason')} required hint={t('tasks.reassignNote')}>
                <Textarea value={note} onChange={(e) => setNote(e.target.value)} disabled={!allowed} />
              </Field>
            )}
          </>
        )}
      </div>
    </FormPage>
  );
}
